import { callMacosBridge } from '#pplx/frontend/apps/ask/computer-app/bridge/macos-adapter.js';
import { getMsOfficeAddinRuntime, openMsOfficeAddinOAuthDialog, } from '#pplx/frontend/apps/ask/oauth-redirect/openMsOfficeAddinOAuthDialog.js';
import { bindDocumentNavigationToActiveAccount } from '#pplx/frontend/browser-storage/accountNavigation.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { getWindowOrigin } from '#pplx/frontend/common/location.js';
import { PopupFailedToOpenError } from '#pplx/frontend/components/utils/popupUtils.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { rewritePerplexityHost } from '#pplx/frontend/platform-components/rewritePerplexityHost.js';
// Storage key written by the backend's oauth_auto_close template as a
// secondary completion signal alongside window.opener.postMessage (popup
// flow) and Office.context.ui.messageParent (Office dialog flow).
export const OAUTH_COMPLETE_STORAGE_KEY = 'pplx:oauth-complete';
const OAUTH_POPUP_POLL_INTERVAL_MS = 500;
const OAUTH_POPUP_COMPLETION_TIMEOUT_MS = 5 * 60 * 1000;
const OAUTH_EXTERNAL_FOCUS_TIMEOUT_MS = OAUTH_POPUP_COMPLETION_TIMEOUT_MS;
export class OAuthIncompleteError extends Error {
    errorCode;
    constructor(errorCode) {
        super('OAuth flow did not complete successfully');
        this.errorCode = errorCode;
        this.name = 'OAuthIncompleteError';
    }
}
export function clearOAuthCompleteStorage() {
    SafeLocalStorage.removeItem(OAUTH_COMPLETE_STORAGE_KEY, { global: true });
}
export function persistOAuthCompleteResult(result) {
    SafeLocalStorage.setItem(OAUTH_COMPLETE_STORAGE_KEY, JSON.stringify({ type: 'oauth-complete', ...result }), { global: true });
}
/**
 * Take the completion signal persisted by the deep-link handlers, clearing it so a
 * later flow cannot read the same result again. This is the only completion evidence
 * available on builds that hand OAuth to the OS browser, where the popup cannot
 * report its own outcome.
 */
export function consumeStoredOAuthCompleteResult(sourceId) {
    const result = getStoredOAuthCompleteResult(sourceId);
    if (result !== undefined)
        clearOAuthCompleteStorage();
    return result;
}
export function persistOAuthCompletionFromCallbackUrl(url) {
    if (url.hostname !== 'auth-complete')
        return false;
    const rawSuccess = url.searchParams.get('success');
    if (rawSuccess !== 'true' && rawSuccess !== 'false')
        return false;
    const errorCode = url.searchParams.get('errorCode') ?? undefined;
    persistOAuthCompleteResult({
        success: rawSuccess === 'true' && errorCode === undefined,
        errorCode,
        connectionUuid: url.searchParams.get('connectionUuid') ?? undefined,
        sourceId: url.searchParams.get('sourceId') ?? undefined,
    });
    return true;
}
function getStoredOAuthCompleteResult(sourceId) {
    const value = SafeLocalStorage.getItem(OAUTH_COMPLETE_STORAGE_KEY, { global: true });
    if (!value)
        return undefined;
    try {
        return getOAuthCompleteResult(JSON.parse(value), sourceId);
    }
    catch {
        return undefined;
    }
}
export function getOAuthCompleteResult(data, sourceId) {
    if (typeof data !== 'object' || data === null)
        return undefined;
    const maybeOAuthComplete = data;
    if (maybeOAuthComplete.type === 'oauth-complete' &&
        typeof maybeOAuthComplete.success === 'boolean' &&
        (!sourceId || !maybeOAuthComplete.sourceId || maybeOAuthComplete.sourceId === sourceId)) {
        return {
            success: maybeOAuthComplete.success,
            sourceId: typeof maybeOAuthComplete.sourceId === 'string' ? maybeOAuthComplete.sourceId : undefined,
            connectionUuid: typeof maybeOAuthComplete.connectionUuid === 'string'
                ? maybeOAuthComplete.connectionUuid
                : undefined,
            autoClose: typeof maybeOAuthComplete.autoClose === 'boolean'
                ? maybeOAuthComplete.autoClose
                : undefined,
            duplicateAccount: maybeOAuthComplete.duplicateAccount === true,
            errorCode: typeof maybeOAuthComplete.errorCode === 'string' ? maybeOAuthComplete.errorCode : undefined,
        };
    }
    return undefined;
}
/** Whether `openRedirectUrl` opens a browser tab for a new-tab redirect with this opener. */
export function usesBrowserPopup(openExternalUrl) {
    return (!import.meta.env.VITE_IS_COMPUTER_APP_BUILD &&
        openExternalUrl === undefined &&
        !import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD &&
        !(import.meta.env.VITE_IS_MS_OFFICE_ADDIN_BUILD && getMsOfficeAddinRuntime()));
}
/**
 * Open a blank tab synchronously in a click handler, for a caller that fetches the
 * OAuth URL afterwards. Null when `openRedirectUrl` would not use a browser popup
 * or the browser blocked it.
 */
export function preOpenRedirectWindow(openExternalUrl) {
    return usesBrowserPopup(openExternalUrl) ? window.open('about:blank', '_blank') : null;
}
export function closePreOpenedWindow(preOpenedWindow) {
    try {
        preOpenedWindow?.close();
    }
    catch {
        // A cross-origin WindowProxy can refuse close() after navigation.
    }
}
function openPopup(url, preOpenedWindow) {
    if (preOpenedWindow == null || preOpenedWindow.closed) {
        return window.open(url, '_blank');
    }
    preOpenedWindow.location.href = url;
    return preOpenedWindow;
}
export function openRedirectUrl({ url, openInNewTab, closeTabOnComplete = false, popupTimeoutErrorCode, openExternalUrl, preOpenedWindow, onRedirect = (url) => redirectWindow(url, 'Enabling connector'), }) {
    url = bindDocumentNavigationToActiveAccount(rewritePerplexityHost(url));
    if (!openInNewTab || !usesBrowserPopup(openExternalUrl)) {
        closePreOpenedWindow(preOpenedWindow);
    }
    if (import.meta.env.VITE_IS_COMPUTER_APP_BUILD && openInNewTab) {
        return callMacosBridge('openInBrowser', { url, waitForFocus: true }).then(() => ({}));
    }
    if (openExternalUrl && openInNewTab) {
        const externalUrlOpener = openExternalUrl;
        // Drop stale popup completion state before handing the OAuth flow to an
        // external browser.
        clearOAuthCompleteStorage();
        return new Promise((resolve, reject) => {
            let completionPoll;
            let completionTimeout;
            let finished = false;
            function cleanup() {
                window.removeEventListener('focus', handleFocus);
                if (completionPoll !== undefined) {
                    clearInterval(completionPoll);
                }
                if (completionTimeout !== undefined) {
                    clearTimeout(completionTimeout);
                }
            }
            function finish(callback) {
                if (finished)
                    return;
                finished = true;
                cleanup();
                callback();
            }
            function consumeStoredCompletion() {
                const storedResult = getStoredOAuthCompleteResult();
                if (storedResult === undefined)
                    return false;
                clearOAuthCompleteStorage();
                finish(() => resolve(storedResult));
                return true;
            }
            function handleFocus() {
                if (consumeStoredCompletion())
                    return;
                if (externalUrlOpener.requiresOAuthCompletionSignal)
                    return;
                finish(() => resolve({ success: true }));
            }
            function handleOpenFailure(error) {
                finish(() => reject(error));
            }
            function handleCompletionTimeout() {
                finish(() => resolve({ success: false }));
            }
            window.addEventListener('focus', handleFocus);
            if (externalUrlOpener.requiresOAuthCompletionSignal) {
                completionPoll = setInterval(consumeStoredCompletion, OAUTH_POPUP_POLL_INTERVAL_MS);
            }
            completionTimeout = setTimeout(handleCompletionTimeout, OAUTH_EXTERNAL_FOCUS_TIMEOUT_MS);
            try {
                void Promise.resolve(externalUrlOpener(url)).catch(handleOpenFailure);
            }
            catch (error) {
                handleOpenFailure(error);
            }
        });
    }
    if (import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD && openInNewTab) {
        clearOAuthCompleteStorage();
        return new Promise((resolve) => {
            window.addEventListener('focus', () => resolve({ success: true }), { once: true });
            window.open(url, '_blank', 'noopener,noreferrer');
        });
    }
    // TODO(PER-30145): fork upstream openRedirectUrl
    if (import.meta.env.VITE_IS_MS_OFFICE_ADDIN_BUILD && openInNewTab) {
        if (getMsOfficeAddinRuntime()) {
            return openMsOfficeAddinOAuthDialog(url);
        }
    }
    if (openInNewTab) {
        clearOAuthCompleteStorage();
        const tab = openPopup(url, preOpenedWindow);
        if (!tab) {
            return Promise.reject(new PopupFailedToOpenError());
        }
        return new Promise((resolve) => {
            let checkOAuthComplete;
            let completionTimeout;
            let finished = false;
            const cleanup = () => {
                if (checkOAuthComplete !== undefined) {
                    clearInterval(checkOAuthComplete);
                }
                if (completionTimeout !== undefined) {
                    clearTimeout(completionTimeout);
                }
                window.removeEventListener('message', handleMessage);
                window.removeEventListener('storage', handleStorage);
            };
            const finish = (result) => {
                if (finished)
                    return;
                finished = true;
                cleanup();
                resolve(result);
            };
            const handleOAuthComplete = (result) => {
                clearOAuthCompleteStorage();
                // Older callback payloads predate autoClose. Preserve their known
                // actionable states while the backend-owned decision rolls out.
                const shouldAutoClose = result.autoClose ?? (!result.duplicateAccount && result.errorCode !== 'session_mismatch');
                if (closeTabOnComplete && shouldAutoClose) {
                    try {
                        tab.close();
                    }
                    catch {
                        // COOP can leave the opener with a stale WindowProxy even though
                        // the storage completion signal is still valid.
                    }
                }
                finish(result);
            };
            const handleMessage = (event) => {
                if (event.source !== tab || event.origin !== getWindowOrigin({ intent: 'document' }))
                    return;
                const result = getOAuthCompleteResult(event.data);
                if (result !== undefined) {
                    handleOAuthComplete(result);
                }
            };
            const handleStoredOAuthComplete = () => {
                const result = getStoredOAuthCompleteResult();
                if (result !== undefined) {
                    handleOAuthComplete(result);
                }
            };
            const handleStorage = (event) => {
                if (event.key !== OAUTH_COMPLETE_STORAGE_KEY || !event.newValue)
                    return;
                handleStoredOAuthComplete();
            };
            window.addEventListener('message', handleMessage);
            window.addEventListener('storage', handleStorage);
            handleStoredOAuthComplete();
            if (finished)
                return;
            // COOP can make a live popup appear closed to its opener, so only an
            // explicit completion signal or the overall timeout ends the flow.
            checkOAuthComplete = setInterval(() => {
                handleStoredOAuthComplete();
            }, OAUTH_POPUP_POLL_INTERVAL_MS);
            completionTimeout = setTimeout(() => {
                finish(popupTimeoutErrorCode ? { success: false, errorCode: popupTimeoutErrorCode } : {});
            }, OAUTH_POPUP_COMPLETION_TIMEOUT_MS);
        });
    }
    onRedirect(url);
    return Promise.resolve({});
}
//# sourceMappingURL=openRedirectUrl.js.map